import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Plus, ExternalLink, Trash2, BarChart3, Pencil, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

export default function SurveysPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/surveys");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this survey and all responses?")) return;
    await api.delete(`/surveys/${id}`);
    load();
  };

  const filtered = query.trim()
    ? items.filter((s) => (s.title || "").toLowerCase().includes(query.trim().toLowerCase()) || (s.description || "").toLowerCase().includes(query.trim().toLowerCase()))
    : items;

  return (
    <div className="space-y-6 fade-in max-w-6xl mx-auto w-full">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-black tracking-tight" style={{ color: "var(--ink-900)" }}>Your surveys</h2>
          <p className="text-sm mt-1" style={{ color: "var(--ink-500)" }}>Build, publish and analyse feedback from every touch point.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 md:flex-none">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--ink-400)" }}/>
            <Input
              data-testid="surveys-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search surveys…"
              className="pl-9 h-10 rounded-full w-full md:w-64"
            />
          </div>
          <Link to="/app/surveys/new">
            <button data-testid="surveys-new-btn" className="btn-pill text-sm whitespace-nowrap">
              <Plus size={16} className="mr-1"/> New survey
            </button>
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="h-44 rounded-3xl bg-slate-100 animate-pulse"/>)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center border-0 rounded-3xl bg-white" style={{ boxShadow: "var(--shadow-soft)" }}>
          <img src="/brand/mascot-builder.jpg" alt="" className="mx-auto w-28 h-28 object-contain mix-blend-multiply"/>
          <h3 className="text-xl font-black mt-2" style={{ color: "var(--ink-900)" }}>{query ? "No matches" : "No surveys yet"}</h3>
          <p className="mt-1" style={{ color: "var(--ink-500)" }}>{query ? "Try a different search term." : "Create your first NPS survey in under a minute."}</p>
          {!query && (
            <Link to="/app/surveys/new">
              <button data-testid="surveys-empty-cta" className="btn-pill mt-4">Create your first survey</button>
            </Link>
          )}
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((s) => (
            <Card key={s.id} data-testid={`survey-card-${s.id}`} className="p-5 border-0 rounded-3xl bg-white transition-all flex flex-col hover:-translate-y-0.5" style={{ boxShadow: "var(--shadow-soft)" }}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-black text-[16px] truncate" style={{ color: "var(--ink-900)" }}>{s.title}</h3>
                  {s.description && <p className="mt-1 text-[12.5px] line-clamp-2" style={{ color: "var(--ink-500)" }}>{s.description}</p>}
                </div>
                <Badge className={`shrink-0 font-black uppercase text-[10px] tracking-wider ${s.status === "published" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : s.status === "archived" ? "bg-slate-100 text-slate-600 hover:bg-slate-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}`}>{s.status || "draft"}</Badge>
              </div>
              <div className="mt-4 flex items-center gap-4 text-[11px]" style={{ color: "var(--ink-400)" }}>
                <span className="inline-flex items-center gap-1 font-semibold"><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "var(--brand)" }}/>{s.questions?.length || 0} qs</span>
                <span className="inline-flex items-center gap-1 font-semibold"><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "var(--secondary)" }}/>{s.response_count || 0} responses</span>
              </div>
              <div className="mt-auto pt-4 flex gap-2">
                <Link to={`/app/surveys/${s.id}`} className="flex-1">
                  <Button data-testid={`survey-edit-${s.id}`} variant="outline" className="w-full rounded-full font-bold h-9"><Pencil size={14} className="mr-1.5"/>Edit</Button>
                </Link>
                <Link to={`/app/surveys/${s.id}/analytics`}>
                  <Button data-testid={`survey-analytics-${s.id}`} variant="outline" size="icon" className="rounded-full h-9 w-9" title="Analytics"><BarChart3 size={16}/></Button>
                </Link>
                {s.status === "published" && s.public_slug && (
                  <a href={`/r/${s.public_slug}`} target="_blank" rel="noreferrer">
                    <Button data-testid={`survey-open-${s.id}`} variant="outline" size="icon" className="rounded-full h-9 w-9" title="Open public link"><ExternalLink size={16}/></Button>
                  </a>
                )}
                <Button data-testid={`survey-delete-${s.id}`} onClick={() => remove(s.id)} variant="outline" size="icon" className="rounded-full h-9 w-9 text-rose-600 hover:text-rose-700 hover:bg-rose-50"><Trash2 size={16}/></Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
